import React, { useState } from 'react'
import { formatDate, formatDateTime } from '@/utils/dateFormat'
import { DataTable } from '@/components/ui/DataTable'
import { Calendar } from 'lucide-react'

interface DataHealthCard {
  name: string
  count: number
  lastSynced: string
  status: 'Success' | 'Failed' | 'Pending'
}

interface AuditLogEntry {
  id: string
  timestamp: string
  user: string
  action: string
  entityType: string
  entityId: string
}

const DataSettings: React.FC = () => {
  const [dataHealth] = useState<DataHealthCard[]>([
    { name: 'Users', count: 1250, lastSynced: new Date(Date.now() - 3600000).toISOString(), status: 'Success' },
    { name: 'Companies', count: 85, lastSynced: new Date(Date.now() - 7200000).toISOString(), status: 'Success' },
    { name: 'Projects', count: 42, lastSynced: new Date(Date.now() - 10800000).toISOString(), status: 'Success' },
  ])

  const [auditLogs] = useState<AuditLogEntry[]>([
    { id: '1', timestamp: new Date().toISOString(), user: 'john.smith@example.com', action: 'Create', entityType: 'User', entityId: 'user-123' },
    { id: '2', timestamp: new Date(Date.now() - 3600000).toISOString(), user: 'jane.doe@example.com', action: 'Update', entityType: 'Company', entityId: 'comp-456' },
    { id: '3', timestamp: new Date(Date.now() - 7200000).toISOString(), user: 'admin@example.com', action: 'Deactivate', entityType: 'User', entityId: 'user-789' },
  ])

  const [filterActionType, setFilterActionType] = useState('All')
  const [filterEntityType, setFilterEntityType] = useState('All')
  const [exportFormat, setExportFormat] = useState<'csv' | 'json'>('csv')

  const handleSync = (entityType: string) => {
    alert(`Syncing ${entityType}...`)
  }

  const handleExport = () => {
    alert(`Exporting audit logs as ${exportFormat.toUpperCase()}...`)
  }

  return (
    <div>
      <h2 style={{ fontSize: '18px', fontWeight: 600, margin: '0 0 24px 0', color: 'var(--color-black)' }}>
        Data settings
      </h2>

      {/* Data health section */}
      <div style={{ marginBottom: '32px' }}>
        <h3 style={{ fontSize: '16px', fontWeight: 600, margin: '0 0 16px 0', color: 'var(--color-black)' }}>
          Common data health
        </h3>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '16px' }}>
          {dataHealth.map((item) => (
            <div
              key={item.name}
              style={{
                padding: '16px',
                border: '1px solid var(--color-light-grey)',
                borderRadius: '8px',
                backgroundColor: 'var(--color-white)',
              }}
            >
              <h4 style={{ fontSize: '16px', fontWeight: 600, margin: '0 0 12px 0', color: 'var(--color-black)' }}>
                {item.name}
              </h4>

              <div style={{ marginBottom: '12px' }}>
                <div style={{ fontSize: '12px', color: 'var(--color-dark-grey)', marginBottom: '4px' }}>
                  Entity count
                </div>
                <div style={{ fontSize: '20px', fontWeight: 700, color: 'var(--color-black)' }}>
                  {item.count}
                </div>
              </div>

              <div style={{ marginBottom: '12px' }}>
                <div style={{ fontSize: '12px', color: 'var(--color-dark-grey)', marginBottom: '4px' }}>
                  Last synced
                </div>
                <div style={{ fontSize: '13px', color: 'var(--color-black)' }}>
                  {formatDate(item.lastSynced)}
                </div>
              </div>

              <div style={{ marginBottom: '12px' }}>
                <span
                  style={{
                    display: 'inline-block',
                    padding: '4px 10px',
                    borderRadius: '4px',
                    fontSize: '12px',
                    fontWeight: 600,
                    backgroundColor: '#dcfce7',
                    color: '#15803d',
                  }}
                >
                  {item.status}
                </span>
              </div>

              <button
                onClick={() => handleSync(item.name)}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  backgroundColor: 'var(--color-magenta)',
                  color: 'var(--color-white)',
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: '13px',
                  fontWeight: 500,
                  cursor: 'pointer',
                  fontFamily: 'var(--font-family)',
                }}
              >
                Sync now
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* Audit log section */}
      <div style={{ marginBottom: '32px' }}>
        <h3 style={{ fontSize: '16px', fontWeight: 600, margin: '0 0 16px 0', color: 'var(--color-black)' }}>
          Audit log
        </h3>

        {/* Filters */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '12px', marginBottom: '16px' }}>
          <select
            value={filterActionType}
            onChange={(e) => setFilterActionType(e.target.value)}
            style={{
              padding: '8px 12px',
              border: '1px solid var(--color-light-grey)',
              borderRadius: '6px',
              fontSize: '14px',
              fontFamily: 'var(--font-family)',
              backgroundColor: 'var(--color-white)',
              cursor: 'pointer',
            }}
          >
            <option value="All">All actions</option>
            <option value="Create">Create</option>
            <option value="Update">Update</option>
            <option value="Delete">Delete</option>
            <option value="Deactivate">Deactivate</option>
          </select>

          <select
            value={filterEntityType}
            onChange={(e) => setFilterEntityType(e.target.value)}
            style={{
              padding: '8px 12px',
              border: '1px solid var(--color-light-grey)',
              borderRadius: '6px',
              fontSize: '14px',
              fontFamily: 'var(--font-family)',
              backgroundColor: 'var(--color-white)',
              cursor: 'pointer',
            }}
          >
            <option value="All">All entity types</option>
            <option value="User">User</option>
            <option value="Company">Company</option>
            <option value="Project">Project</option>
          </select>

          <input
            type="date"
            placeholder="From date"
            style={{
              padding: '8px 12px',
              border: '1px solid var(--color-light-grey)',
              borderRadius: '6px',
              fontSize: '14px',
              fontFamily: 'var(--font-family)',
              backgroundColor: 'var(--color-white)',
            }}
          />

          <input
            type="date"
            placeholder="To date"
            style={{
              padding: '8px 12px',
              border: '1px solid var(--color-light-grey)',
              borderRadius: '6px',
              fontSize: '14px',
              fontFamily: 'var(--font-family)',
              backgroundColor: 'var(--color-white)',
            }}
          />

          <input
            type="text"
            placeholder="Search user..."
            style={{
              padding: '8px 12px',
              border: '1px solid var(--color-light-grey)',
              borderRadius: '6px',
              fontSize: '14px',
              fontFamily: 'var(--font-family)',
            }}
          />
        </div>

        {/* Audit log table */}
        <DataTable
          columns={[
            { key: 'timestamp', header: 'Timestamp', render: (value: string) => formatDateTime(value) },
            { key: 'user', header: 'User', render: (value: string) => <div style={{ fontWeight: 500 }}>{value}</div> },
            { key: 'action', header: 'Action', render: (value: string) => value },
            { key: 'entityType', header: 'Entity type', render: (value: string) => value },
            { key: 'entityId', header: 'Entity ID', render: (value: string) => <code style={{ fontSize: '12px', backgroundColor: '#f3f4f6', padding: '2px 6px', borderRadius: '3px' }}>{value}</code> },
          ]}
          data={auditLogs}
          rowKey="timestamp"
        />
      </div>

      {/* Export section */}
      <div style={{ marginBottom: '32px' }}>
        <h3 style={{ fontSize: '16px', fontWeight: 600, margin: '0 0 16px 0', color: 'var(--color-black)' }}>
          Export
        </h3>

        <div style={{ display: 'flex', gap: '12px' }}>
          <select
            value={exportFormat}
            onChange={(e) => setExportFormat(e.target.value as 'csv' | 'json')}
            style={{
              padding: '8px 12px',
              border: '1px solid var(--color-light-grey)',
              borderRadius: '6px',
              fontSize: '14px',
              fontFamily: 'var(--font-family)',
              backgroundColor: 'var(--color-white)',
              cursor: 'pointer',
            }}
          >
            <option value="csv">CSV</option>
            <option value="json">JSON</option>
          </select>

          <button
            onClick={handleExport}
            style={{
              padding: '8px 16px',
              backgroundColor: 'var(--color-magenta)',
              color: 'var(--color-white)',
              border: 'none',
              borderRadius: '6px',
              fontSize: '14px',
              fontWeight: 500,
              cursor: 'pointer',
              fontFamily: 'var(--font-family)',
            }}
          >
            Export audit log
          </button>
        </div>
      </div>

      {/* Data retention section */}
      <div style={{ padding: '16px', border: '1px solid var(--color-light-grey)', borderRadius: '8px', backgroundColor: '#f9fafb' }}>
        <h3 style={{ fontSize: '16px', fontWeight: 600, margin: '0 0 8px 0', color: 'var(--color-black)' }}>
          Data retention
        </h3>
        <p style={{ margin: '0 0 8px 0', fontSize: '14px', color: 'var(--color-dark-grey)' }}>
          Audit log retention: <strong>2 years (minimum, not configurable)</strong>
        </p>
        <p style={{ margin: 0, fontSize: '12px', color: 'var(--color-dark-grey)', fontStyle: 'italic' }}>
          Audit logs are retained for a minimum of 2 years for compliance purposes and cannot be modified.
        </p>
      </div>
    </div>
  )
}

export default DataSettings
